/**
 * TestHistoryPopup.tsx
 *
 * Floating popup matching legacy toggleTestHistory (lines 2591-2768).
 * Shows up to 10 historical values for a test, with a chart link.
 */
import { useEffect, useRef, useCallback } from 'react';
import { BarChart3, X } from 'lucide-react';
import type { BiomarkerResult } from '@/services/labs.service';

function formatDate(value: string): string {
  const d = new Date(value + 'T00:00:00');
  return isNaN(d.getTime()) ? value : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface TestHistoryPopupProps {
  testName: string;
  history: BiomarkerResult[];
  anchorRect: DOMRect | null;
  onClose: () => void;
  onViewInChart: (testName: string) => void;
}

export function TestHistoryPopup({
  testName,
  history,
  anchorRect,
  onClose,
  onViewInChart,
}: TestHistoryPopupProps) {
  const popupRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (popupRef.current && !popupRef.current.contains(e.target as Node)) {
      onClose();
    }
  }, [onClose]);

  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [handleClickOutside, handleEscape]);

  if (!anchorRect) return null;

  const entries = history
    .sort((a, b) => b.date.localeCompare(a.date))
    .slice(0, 10);

  const popupWidth = 420;
  const popupMaxHeight = 400;
  let left = anchorRect.left + anchorRect.width / 2 - popupWidth / 2;
  let top = anchorRect.bottom + 8;

  if (left < 8) left = 8;
  if (left + popupWidth > window.innerWidth - 8) left = window.innerWidth - popupWidth - 8;
  if (top + popupMaxHeight > window.innerHeight - 8) {
    top = anchorRect.top - popupMaxHeight - 8;
    if (top < 8) top = 8;
  }

  return (
    <div
      ref={popupRef}
      className="fixed z-50 bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden"
      style={{ left, top, width: popupWidth, maxHeight: popupMaxHeight }}
    >
      <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
        <h4 className="text-sm font-semibold text-slate-800 truncate flex-1 mr-2">{testName}</h4>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => onViewInChart(testName)}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors"
            title="View in History chart"
          >
            <BarChart3 className="h-3.5 w-3.5" />
            Chart
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-0.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div className="overflow-y-auto" style={{ maxHeight: popupMaxHeight - 45 }}>
        {entries.length === 0 ? (
          <p className="px-4 py-3 text-sm text-slate-400 italic">No history available.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr className="text-left text-[11px] font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">Value</th>
                <th className="px-4 py-2">Source</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.map((entry, i) => (
                <tr key={`${entry.date}-${i}`} className="hover:bg-slate-50">
                  <td className="px-4 py-2 text-slate-600">{formatDate(entry.date)}</td>
                  <td className="px-4 py-2 text-slate-900 font-semibold">{entry.value}</td>
                  <td className="px-4 py-2 text-slate-500 text-xs">{entry.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="px-4 py-2 bg-slate-50 border-t border-slate-200 text-[11px] text-slate-400">
        {entries.length} of {history.length} entries shown
      </div>
    </div>
  );
}
